import type { ColDef } from "ag-grid-enterprise";
import debounce from "lodash/debounce";
import { useEffect, useMemo, useState } from "react";

import { ColumnDropdown, Input, Label, SelectInput } from "@web-app/components";

import SidebarTabs from "./Tabs";
import { GroupParameter, SidebarMode, SortParameter } from "./useOrderSidebar";
import { SortOptionType, groupOptions, sortOptions } from "./utils";

export default function Header({
  columnDefs,
  groupParameter,
  searchTerm,
  setColumnDefs,
  setGroupParameter,
  setSearchTerm,
  setSidebarMode,
  setSortParameter,
  sidebarMode,
  sortParameter,
}: {
  columnDefs?: ColDef[];
  groupParameter: GroupParameter;
  searchTerm: string;
  setColumnDefs?: (c: ColDef[]) => void;
  setGroupParameter: (value: GroupParameter) => void;
  setSearchTerm: (value: string) => void;
  setSidebarMode: (value: SidebarMode) => void;
  setSortParameter: (value: SortOptionType) => void;
  sidebarMode: SidebarMode;
  sortParameter: SortParameter;
}) {
  const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm);

  // Debounced search handler
  const debouncedSetSearchTerm = useMemo(
    () =>
      debounce((value: string) => {
        setSearchTerm(value);
      }, 500),
    [setSearchTerm],
  );

  // Update local state immediately, debounce the parent update
  const handleSearchChange = (value: string) => {
    setLocalSearchTerm(value);
    debouncedSetSearchTerm(value);
  };

  // Sync local state if searchTerm is changed externally
  useEffect(() => {
    setLocalSearchTerm(searchTerm);
  }, [searchTerm]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedSetSearchTerm.cancel();
    };
  }, [debouncedSetSearchTerm]);

  return (
    <div className="sticky top-0 bg-white px-4 pb-2 z-20">
      <div className="col-span-12 w-full mt-3 lg:mt-1">
        <Input
          type="search"
          placeholder="Search by Assignee, Address, ID, Recipient or External ID"
          onChange={handleSearchChange}
          value={localSearchTerm}
        />
      </div>
      <div className="flex justify-between items-center mt-2">
        <span className="hidden lg:block">
          <SidebarTabs selectedTab={sidebarMode} onSelectTab={setSidebarMode} />
        </span>

        {sidebarMode === "list" && (
          <div className="flex justify-between gap-3">
            <div className="flex items-center gap-2">
              <Label className="font-normal !text-gray-400 text-xs">
                Sort By
              </Label>
              <SelectInput
                required
                options={sortOptions}
                selected={sortParameter.value}
                onChange={(value) => {
                  const sortOption = sortOptions.find(
                    (option) => option.value === value,
                  );
                  if (sortOption) {
                    setSortParameter(sortOption);
                  }
                }}
                variant="inline"
                size="xs"
              />
            </div>
            <div className="flex items-center gap-2">
              <Label className="font-normal !text-gray-400 text-xs">
                Group By
              </Label>
              <SelectInput
                required
                options={groupOptions}
                selected={groupParameter.value}
                onChange={(value) => {
                  const groupOption = groupOptions.find(
                    (option) => option.value === value,
                  );
                  if (groupOption) {
                    setGroupParameter(groupOption);
                  }
                }}
                variant="inline"
                size="xs"
              />
            </div>
          </div>
        )}

        {sidebarMode === "table" && (
          <div className="flex justify-between gap-3">
            <div className="flex items-center gap-2">
              <Label className="font-normal !text-gray-400 text-xs">
                Sort By
              </Label>
              <SelectInput
                required
                options={sortOptions}
                selected={sortParameter.value}
                onChange={(value) => {
                  const sortOption = sortOptions.find(
                    (option) => option.value === value,
                  );
                  if (sortOption) {
                    setSortParameter(sortOption);
                  }
                }}
                variant="inline"
                size="xs"
              />
            </div>
            <div className="flex items-center gap-2">
              {columnDefs && setColumnDefs && (
                <div className="flex justify-end">
                  <ColumnDropdown
                    columnDefs={columnDefs}
                    setColumnDefs={setColumnDefs}
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
